import { useState } from "react";
import { PaginationButtons } from "../../../components/ui/PaginationButtons";
import { useGetEmpesasQuery } from "../empresaApi";
import { EmpresaFilters } from "../../../types/empresa";
import { Link, useLocation } from "react-router";
import { CircularProgress } from "@mui/joy";
import { ErrorMessage } from "../../../components/errors/ErrorMessage";
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';

interface Props {
    filters: EmpresaFilters;
}

export function EmpresasTable({ filters }: Props) {
    const location = useLocation();

    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);

    const { data, isFetching, isSuccess, isError } = useGetEmpesasQuery({...filters, pageNumber, pageSize });

    if(isFetching)
        return (
            <div className="h-full w-full flex justify-center items-center">
                <CircularProgress />
            </div>
        );

    if(isError || !isSuccess)
        return (
            <div className="w-full m-4">
                <ErrorMessage>
                    Ha ocurrido un error al cargar la información
                </ErrorMessage>
            </div>
        );

    return (
        <div className="border border-gray-400 overflow-auto flex-1">
            {isSuccess && data.empresas.length == 0
            ?
            <div className="w-full">
                <p className="text-center py-4 text-2xl font-semibold">No hay empresas registradas</p>
            </div>
            :
            <table className="w-full">
                <thead className="">
                    <tr>
                        <th className="w-[30%]s border border-gray-400 h-[10px] py-[4px] px-[8px] text-center text-[14px] font-semibold text-[#111827]">
                            Empresa
                        </th>
                        <th className="border border-gray-400 h-[10px] py-[4px] px-[8px] text-center text-[14px] font-semibold text-[#111827]">
                            Sucursal
                        </th>
                        <th className="w-[140px]s border border-gray-400 h-[10px] py-[4px] px-[8px] text-center text-[14px] font-semibold text-[#111827]">
                            Num. Trabajadores
                        </th>
                        <th className="w-32s border border-gray-400 h-[10px] py-[4px] px-[8px] text-center text-[14px] font-semibold text-[#111827]">
                            Activa/Inactiva
                        </th>
                        <th className="w-[160px]s border border-gray-400 h-[10px] py-[4px] px-[8px] text-center text-[14px] font-semibold text-[#111827]">
                            Ultima fecha de visita
                        </th>
                        <th className="w-52s border border-gray-400 h-[10px] py-[4px] px-[8px] text-center text-[14px] font-semibold text-[#111827]">
                            Prospectador asignado
                        </th>
                        <th className="w-36s border border-gray-400 h-[10px] py-[4px] px-[8px] text-center text-[14px] font-semibold text-[#111827]">
                            Frecuencia de visita
                        </th>
                        <th className="w-36s border border-gray-400 h-[10px] py-[4px] px-[8px] text-center text-[14px] font-semibold text-[#111827]">
                            Fecha de creación
                        </th>
                        <th className="w-10 border border-gray-400 h-[10px] py-[4px] px-[8px] text-center text-[14px] font-semibold text-[#111827]">
                            <SettingsRoundedIcon sx={{ height: 17, width: 17, color: "#111827" }}/>
                        </th>
                    </tr>
                </thead>

                <tbody className="text-[14px]">
                    {data.empresas.map((empresa, index) => (
                    <tr key={index}>
                        <td className="border h-[10px] py-[4px] px-[8px] text-[14px] font-medium">
                            {empresa.nombre}
                        </td>
                        <td className="border h-[10px] py-[4px] px-[8px] text-[14px] font-semibold">
                            {empresa.sucursal}
                        </td>
                        <td className="border h-[10px] py-[4px] px-[8px] text-[14px] font-semibold text-center">
                            {empresa.numTrabajadores}
                        </td>
                        <td className="border h-[10px] py-[4px] px-[8px] text-[14px] font-semibold">
                            {empresa.estado}
                        </td>
                        <td className="border h-[10px] py-[4px] px-[8px] text-[14px] font-semibold">
                            {empresa.ultimaVisita ?? "Sin Visitas"}
                        </td>
                        <td className="border h-[10px] py-[4px] px-[8px] text-[14px] font-semibold">
                            {empresa.nombreProspectador ?? "Sin Prospectador"}
                        </td>
                        <td className="border h-[10px] py-[4px] px-[8px] text-[14px] font-semibold">
                            {empresa.frecuenciaVisita}
                        </td>
                        <td className="border h-[10px] py-[4px] px-[8px] text-[14px] font-semibold">
                            {empresa.fechaCreacion}
                        </td>
                        <td className="text-center border h-[10px] py-[4px] px-[8px] text-[14px] font-semibold">
                            <Link 
                                to={`/empresa/${empresa.id}`}
                                state={{ fromName: "empresas", fromPath: location.pathname }}>
                                Detalles
                            </Link>
                        </td>
                    </tr>
                    ))}
                </tbody>
            </table>
            }

            <div className="flex flex-col items-center justify-center w-full px-3 py-1">
                <PaginationButtons
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                    setPageNumber={setPageNumber}
                    setPageSize={setPageSize}
                    ultimaPagina={data?.ultimaPagina ?? 0} />
                <p className="text-gray-800 font-semibold text-[13px]">
                    Total de resultados: {data.totalEmpresas}
                </p>
            </div>
        </div>
    );
}
